import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
})
export class BreadcrumbsComponent implements OnInit, OnChanges {
  breadcrumbItems: any[] = [];
  collapseItems: any[] = [];
  @Input() data;
  @Input() directorylocation;
  @Input() privateOffice;
  @Output() breadCrumbId = new EventEmitter<any>();

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.setupBreadcrumbList(changes?.['data']?.currentValue);
  }

  ngOnInit(): void {}

  /**
   * Sets up the breadcrumb list based on the provided folder tree data.
   * It organizes breadcrumbs into a main list and a collapsed list for better navigation.
   *
   * @param {any[]} data - The folder tree data containing breadcrumb information.
   */
  setupBreadcrumbList(data) {
    var folderTree = data; // Stores the folder structure data
    this.breadcrumbItems = []; // Array to hold the visible breadcrumbs
    this.collapseItems = []; // Array to hold the collapsed breadcrumb items

    let currentUrl = this.router.url; // Get the current URL
    let breadCrumbToShow = 3; // Default number of breadcrumbs to display

    // Adjusts breadcrumb display count if the current URL matches a specific path
    if (currentUrl.includes('private/office')) {
      breadCrumbToShow = 6;
    }

    // Checks if folderTree is defined and has at least one item
    if (folderTree !== undefined && folderTree != null && folderTree?.length > 0) {
      // If folderTree has more items than the allowed visible breadcrumbs
      if (folderTree?.length > breadCrumbToShow) {
        for (let index = 0; index < folderTree?.length; index++) {
          // Pushes items to collapseItems until the last two remain
          if (index < folderTree.length - 2) {
            this.collapseItems?.push({
              label: folderTree[index]?.label,
              route: folderTree[index]?.url,
              icon: folderTree[index]?.icon,
              command: e => this.takeAction(e?.item),
            });
          } else {
            // Pushes the last two items to breadcrumbItems
            this.breadcrumbItems?.push({
              label: folderTree[index]?.label,
              route: folderTree[index]?.url,
              icon: folderTree[index]?.icon,
              command: e => this.takeAction(e?.item),
            });
          }
        }

        // Adds 'Library' as the first breadcrumb and an ellipsis icon for the collapsed items
        this.breadcrumbItems?.unshift(
          { label: 'Library', route: '', icon: 'pi pi-folder-open', command: e => this.takeAction(e) },
          { label: '', route: '', icon: 'pi pi-ellipsis-h', command: e => this.takeAction(e?.item) }
        );
      } else {
        // If folderTree length is within the allowed limit, directly push all items
        this.breadcrumbItems.push({
          label: 'Library',
          route: '',
          icon: 'pi pi-folder-open',
          command: e => this.takeAction(e?.item),
        });

        // Adds all folderTree items to breadcrumbItems
        for (let index = 0; index < folderTree?.length; index++) {
          this.breadcrumbItems.push(folderTree[index]);
        }
      }
    } else {
      // If folderTree is empty or undefined, add only the 'Library' breadcrumb
      this.breadcrumbItems.push({
        label: 'Library',
        route: '',
        icon: 'pi pi-folder-open',
        command: e => this.takeAction(e?.item),
      });
    }
  }

  /**
   * Handles breadcrumb navigation and emits breadcrumb ID if applicable.
   *
   * @param {any} event - The breadcrumb item or navigation event.
   */
  takeAction(event) {
    // Checks if the user is in the private office section
    if (this.privateOffice) {
      // Ensures directory location is defined and not empty
      if (this.directorylocation !== undefined && this.directorylocation != null && this.directorylocation != '') {
        // Handles navigation based on different directory locations
        if (this.directorylocation == 'useroffice') {
          // Navigates to the user's private office with an optional ID parameter
          this.router.navigateByUrl(
            `/library/private/office${
              event?.route?.trim()?.length > 0 || event?.url?.trim()?.length > 0
                ? `?Id=${event?.route?.trim().length > 0 ? event?.route?.trim() : event?.url?.trim()}`
                : ''
            }`
          );
        } else if (this.directorylocation == 'folder') {
          // Navigates to the private library folder with an optional ID parameter
          this.router.navigateByUrl(
            `/library/private${
              event?.route?.trim()?.length > 0 || event?.url?.trim()?.length > 0
                ? `?Id=${event?.route?.trim().length > 0 ? event?.route?.trim() : event?.url?.trim()}`
                : ''
            }`
          );
        } else if (this.directorylocation == 'shared') {
          // Special case for 'Library' breadcrumb navigation in the shared section
          if (event?.route == '' && event?.label == 'Library' && (!event?.url || event?.url === '')) {
            let currentUrl = 'library/private/shared';

            // Navigates to a blank route first to refresh the state before navigating to the shared library
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigate([currentUrl]);
            });
          } else {
            // Navigates to the shared library with an optional ID parameter
            this.router.navigateByUrl(
              `/library/private/shared${
                event?.route?.trim()?.length > 0 || event?.url?.trim()?.length > 0
                  ? `?Id=${event?.route?.trim().length > 0 ? event?.route?.trim() : event?.url?.trim()}`
                  : ''
              }`
            );
          }
        }
      }
    } else {
      // If not in privateOffice, emits the breadcrumb ID
      let id = event?.route?.trim().length > 0 ? event?.route?.trim() : event?.url?.trim();
      this.breadCrumbId.emit(id);
    }
  }
}
